/**
 * Config — one resolved, validated, frozen object per component. FAILS CLOSED.
 *
 * `static config = { key: { type, default, required, min, max, oneOf } }` is the schema.
 * Types: string, number, boolean, url, enum, json.
 * Sources, later wins:  schema defaults
 *   -> value attributes   data-<identifier>-<key>-value
 *   -> inline JSON        <script type="application/json" id="…"> named by data-<identifier>-config-id-value
 *   -> config URL         data-<identifier>-config-url-value
 * Reserved value names: config-id, config-url, start.
 *
 * An unknown key, an invalid value, a missing required key or a failed config URL is an ERROR:
 * the component shows `error` (kind `config`) and starts nothing. Errors name the key and the source,
 * never the value.
 *
 * @typedef {{ type: 'string'|'number'|'boolean'|'url'|'enum'|'json', default?: unknown, required?: boolean,
 *             oneOf?: unknown[], min?: number, max?: number }} Spec
 * @typedef {Record<string, Spec>} Schema
 * @typedef {{ source: 'attribute'|'script'|'url', key: string, reason: 'unknown'|'invalid'|'missing'|'unreadable'|'remote' }} ConfigError
 * @typedef {{ config: Readonly<Record<string, any>>, errors: ConfigError[] }} Resolved
 */
import { apiUrl } from './http.js'

export const START_MODES = Object.freeze(['connect', 'visible', 'idle', 'interaction'])
/** `start` and `refreshKey` are reserved: every component has them (`start` is `visible` unless the schema says otherwise). */
const RESERVED = {
  start: { type: 'enum', oneOf: START_MODES, default: 'visible' },
  refreshKey: { type: 'string', default: '' }, // a window `ls:refresh` listing this key restarts the component
}

const kebab = (key) => key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
const camel = (key) => key.replace(/-([a-z0-9])/g, (_, letter) => letter.toUpperCase())
const isObject = (value) => typeof value === 'object' && value !== null && !Array.isArray(value)

/** Attribute text -> a value of the declared type (undefined when it cannot be read). */
function fromAttribute(type, text) {
  if (type === 'string' || type === 'url' || type === 'enum') return text
  if (type === 'number') return text.trim() === '' ? undefined : Number(text)
  if (type === 'boolean') return { true: true, 1: true, '': true, false: false, 0: false }[text]
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

/** Returns the accepted value, or undefined when it does not satisfy the spec. */
function check(spec, value) {
  const { type, oneOf, min = -Infinity, max = Infinity } = spec
  if (type === 'url') {
    if (value === '') return spec.required ? undefined : ''
    value = apiUrl(value) ?? undefined
  } else if (type === 'number') {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) return undefined
  } else if (type === 'string') {
    if (typeof value !== 'string') return undefined
  } else if (type === 'boolean') {
    if (typeof value !== 'boolean') return undefined
  } else if (type === 'enum') {
    if (typeof value !== 'string') return undefined
  } else if (type !== 'json') return undefined
  if (value === undefined) return undefined
  if (oneOf && !oneOf.includes(value)) return undefined
  return value
}

function deepFreeze(value) {
  if (typeof value === 'object' && value !== null && !Object.isFrozen(value)) {
    Object.freeze(value)
    Object.values(value).forEach(deepFreeze)
  }
  return value
}

/** @param {object} a @param {object} b */
export const sameConfig = (a, b) => JSON.stringify(a) === JSON.stringify(b)

/**
 * The schema of a controller class: `static config` of every class on the prototype chain
 * (base first, subclass wins) plus the reserved `start`.
 * @param {Function} controllerClass
 * @returns {Schema}
 */
export function schemaOf(controllerClass) {
  const chain = []
  for (let c = controllerClass; c && c !== Function.prototype; c = Object.getPrototypeOf(c)) {
    if (Object.hasOwn(c, 'config') && isObject(c.config)) chain.unshift(c.config)
  }
  return Object.assign({}, RESERVED, ...chain)
}

/**
 * @param {Element} element the controller element
 * @param {string} identifier the controller identifier
 * @param {Schema} schema
 */
export function configSource(element, identifier, schema) {
  schema = { ...RESERVED, ...schema } // `start` is reserved: always part of the schema
  for (const [key, spec] of Object.entries(schema)) {
    if (spec.type === 'enum' && !Array.isArray(spec.oneOf)) throw new TypeError(`config: enum "${key}" needs oneOf`)
  }
  const keys = Object.keys(schema)
  const prefix = `data-${identifier}-`
  const attribute = (key) => `${prefix}${kebab(key)}-value`
  const ID_ATTRIBUTE = attribute('configId')
  const URL_ATTRIBUTE = attribute('configUrl')

  /** Merge one untrusted source into config. */
  function layer(config, errors, source, raw) {
    if (!isObject(raw)) return void errors.push({ source, key: '*', reason: 'unreadable' })
    for (const key of Object.keys(raw)) {
      if (!Object.hasOwn(schema, key)) errors.push({ source, key, reason: 'unknown' })
    }
    for (const key of keys) {
      if (!Object.hasOwn(raw, key)) continue
      const value = check(schema[key], raw[key])
      if (value === undefined) errors.push({ source, key, reason: 'invalid' })
      else config[key] = value
    }
  }

  /** Defaults + attributes + inline JSON. Synchronous. */
  function local() {
    /** @type {Record<string, any>} */
    const config = {}
    /** @type {ConfigError[]} */
    const errors = []
    for (const key of keys) {
      if (schema[key].default !== undefined) config[key] = structuredClone(schema[key].default)
    }

    const attributes = {}
    for (const { name, value } of element.attributes) {
      if (!name.startsWith(prefix) || !name.endsWith('-value')) continue
      const key = camel(name.slice(prefix.length, -'-value'.length))
      if (key === 'configId' || key === 'configUrl') continue
      if (!Object.hasOwn(schema, key)) errors.push({ source: 'attribute', key, reason: 'unknown' })
      else attributes[key] = fromAttribute(schema[key].type, value)
    }
    for (const key of Object.keys(attributes)) {
      if (attributes[key] === undefined) {
        errors.push({ source: 'attribute', key, reason: 'invalid' })
        delete attributes[key]
      }
    }
    layer(config, errors, 'attribute', attributes)

    const id = element.getAttribute(ID_ATTRIBUTE)
    if (id) {
      const script = document.getElementById(id)
      let json
      try {
        if (script?.getAttribute('type') === 'application/json') json = JSON.parse(script.textContent ?? '')
      } catch {
        json = undefined
      }
      layer(config, errors, 'script', json)
    }
    return { config, errors }
  }

  /** Required keys must have a value once every source is merged. */
  function finish(config, errors) {
    for (const key of keys) {
      if (schema[key].required && (config[key] === undefined || config[key] === '')) {
        errors.push({ source: 'attribute', key, reason: 'missing' })
      }
    }
    return { config: deepFreeze(config), errors }
  }

  return {
    /** Is there a remote source to load? */
    hasRemote: () => Boolean(element.getAttribute(URL_ATTRIBUTE)),

    /** @returns {Resolved} defaults + attributes + inline JSON (a config URL is NOT loaded here: errors then include `remote`) */
    local() {
      const { config, errors } = local()
      if (this.hasRemote()) errors.push({ source: 'url', key: '*', reason: 'remote' })
      return finish(config, errors)
    },

    /**
     * All sources. A failed config URL is an error (fail closed): no defaults take over.
     * @param {ReturnType<typeof import('./http.js').createHttp>} http
     * @returns {Promise<Resolved | { stale: true }>}
     */
    async load(http) {
      const { config, errors } = local()
      const url = element.getAttribute(URL_ATTRIBUTE)
      if (url) {
        const result = await http.get(url, { lane: `config:${identifier}` })
        if (result.stale) return { stale: true }
        if (!result.ok) errors.push({ source: 'url', key: '*', reason: 'remote' })
        else layer(config, errors, 'url', result.data)
      }
      return finish(config, errors)
    },

    /**
     * Call onChange when a `data-<identifier>-*-value` attribute of the element changes at runtime
     * (declared or not: an unknown key added later must fail closed too). Only attributes are watched,
     * not the content of the JSON script or the config URL's answer.
     * @param {import('./lifetime.js').Lifetime} lifetime
     * @param {() => void} onChange
     */
    watch(lifetime, onChange) {
      const relevant = (name) => name.startsWith(prefix) && name.endsWith('-value')
      const observer = new MutationObserver((records) => {
        if (lifetime.active && records.some((record) => relevant(record.attributeName))) onChange()
      })
      lifetime.observe(observer, element, { attributes: true })
    },
  }
}
